import { Bell, EyeEmpty } from 'iconoir-react';
import moment from 'moment';
import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux';
import { FAIL, LOADING, SUCCESS } from '../../../redux_store/actionTypes';
import { clearNotiState } from '../../../redux_store/notifications/notifications_slice';
import { createNotification, getNotifications } from '../../../redux_store/notifications/notifications_thunk';
import Button from '../../templates/Button';
import CreateNotificationModal from '../../templates/modals/CreateNotificationModal';
import LoadingModal from '../../templates/modals/LoadingModal';
import ViewNotificationModal from '../../templates/modals/ViewNotificationModal';
import Tabs from '../../templates/Tabs'
import toast from '../../templates/ToastMessage'
import { avatar1, avatar2, avatar3 } from '../../../assets'

const NotificationPage = () => {
  const dispatch = useDispatch();
  const [showMessageModal, setShowMessageModal] = useState({ visible: false, notification: null })
  const [showNewNotification, setShowNewNotification] = useState(false);
  const [tabSelected, setTabSelected] = useState('All');
  const { status, message, notifications } = useSelector((state) => state.notification);

  const handleTabSelection = (e) => {
    const channel = e.target.innerText;
    dispatch(getNotifications(channel));
    setTabSelected(channel);
  }

  useEffect(() => {
    if (status === FAIL) {
      toast({ message, status });
      dispatch(clearNotiState());
    } else if (status === SUCCESS) {
      setShowNewNotification(false);
      toast({ message, status });
      dispatch(clearNotiState());
    }
    // eslint-disable-next-line
  }, [status, message]);

  const getChannelBadge = (channel) => {
    const styles = {
      'SMS Notification': 'badge bg-purple-100 text-purple-700',
      'Email Notification': 'badge bg-blue-100 text-blue-700',
      'App Notification': 'badge bg-green-100 text-green-700',
    };
    return styles[channel] || 'badge bg-slate-100 text-slate-600';
  };

  return (
    <div className='space-y-6'>
      {status === LOADING && <LoadingModal message={'Please wait...'} />}
      {showMessageModal.visible &&
        <ViewNotificationModal
          notification={showMessageModal.notification}
          onCancel={() => setShowMessageModal({ visible: false, notification: null })}
        />
      }
      {showNewNotification && <CreateNotificationModal
        onSubmit={(e, details) => {
          e.preventDefault();
          if (details.to === '') {
            return toast({ message: "Kindly select broadcast category" });
          }
          dispatch(createNotification(details));
        }}
        onCancel={() => setShowNewNotification(false)}
      />}

      {/* Page Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-2xl font-bold text-surface-800'>Notifications</h1>
          <p className='text-surface-500 mt-1'>Manage and send notifications to users</p>
        </div>
        <Button
          icon={<Bell strokeWidth={2} className='w-4 h-4' />}
          onClick={() => setShowNewNotification(true)}
          variant='primary'
          label={'Create New'}
        />
      </div>

      {/* Filters */}
      <div className='card'>
        <Tabs
          active={tabSelected}
          heading={['All', 'SMS Notification', 'Email Notification', 'App Notification']}
          onTabSelect={handleTabSelection}
        />
      </div>

      {/* Notifications Table */}
      <div className='card p-0 overflow-hidden'>
        <table className="table-modern">
          <thead>
            <tr>
              <th>From</th>
              <th>To</th>
              <th>Channel</th>
              <th>Message</th>
              <th>Date</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {notifications && notifications.length > 0 ?
              notifications.map((notification, index) =>
                <tr key={notification.id || index}>
                  <td>
                    <div className="flex items-center gap-3">
                      <img
                        className="w-10 h-10 rounded-xl object-cover ring-2 ring-surface-100"
                        src={notification.admin.profile_pic}
                        alt=""
                        loading="lazy"
                      />
                      <div>
                        <p className="font-semibold text-surface-800">
                          {notification.admin.lastname} {notification.admin.firstname}
                        </p>
                        <p className="text-xs text-surface-400">
                          {notification.admin.email}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td>
                    {notification.user ? (
                      <div className="flex items-center gap-3">
                        <img
                          className="w-10 h-10 rounded-xl object-cover ring-2 ring-surface-100"
                          src={notification.user.profile_pic}
                          alt=""
                          loading="lazy"
                        />
                        <div>
                          <p className="font-semibold text-surface-800">
                            {notification.user.surname} {notification.user.other_names}
                          </p>
                          <p className="text-xs text-surface-400">
                            {notification.user.email}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="flex -space-x-2 mb-1">
                          <img className="w-8 h-8 rounded-full border-2 border-white" src={avatar1} alt="" />
                          <img className="w-8 h-8 rounded-full border-2 border-white" src={avatar2} alt="" />
                          <img className="w-8 h-8 rounded-full border-2 border-white" src={avatar3} alt="" />
                        </div>
                        <span className='text-xs text-surface-500'>{notification.broadcast}</span>
                      </div>
                    )}
                  </td>
                  <td>
                    <span className={getChannelBadge(notification.channel)}>
                      {notification.channel?.replace(' Notification', '')}
                    </span>
                  </td>
                  <td>
                    <p className='max-w-[200px] line-clamp-2 text-surface-600'>
                      {notification.message}
                    </p>
                  </td>
                  <td className='text-surface-500'>
                    {moment(notification.createdAt).format('MMM D, YYYY')}
                  </td>
                  <td>
                    <button
                      onClick={() => setShowMessageModal({ visible: true, notification })}
                      className='btn-ghost p-2 rounded-lg'
                      title='View Details'
                    >
                      <EyeEmpty strokeWidth={2} className='w-4 h-4' />
                    </button>
                  </td>
                </tr>
              ) :
              <tr>
                <td colSpan={6} className="text-center py-12">
                  <div className='flex flex-col items-center gap-3'>
                    <div className='w-16 h-16 rounded-2xl bg-surface-100 flex items-center justify-center'>
                      <Bell strokeWidth={1.5} className='w-8 h-8 text-surface-400' />
                    </div>
                    <div>
                      <p className='font-medium text-surface-600'>No notifications</p>
                      <p className='text-sm text-surface-400'>Send your first notification</p>
                    </div>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default NotificationPage
